import { Alert, Badge, Button, EmptyState, Field, Input, Skeleton } from '@personachat/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, ExternalLink, Package, Plus, Repeat, Tag } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components } from '../../api/schema';
import { useSession } from '../../auth/AuthProvider';
import { Code, ErreurChargement, InfoList, Section, Select } from '../../components/bits';
import { DataTable, type Colonne } from '../../components/DataTable';
import { Drawer } from '../../components/Drawer';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { centimesDepuisSaisie, formatDateTime, formatEuros, formatNombre, formatPrixCredit, saisieEuros } from '../../lib/format';
import { libelleDureePack, lienStripe, LIBELLE_PERIODICITE, PAR_PERIODE } from '../../lib/labels';
import { AuditTimeline } from '../audit/AuditTimeline';

import { useVentes, VentesEntete } from './VentesEntete';

type Pack = components['schemas']['PackListItem'];
type Formule = components['schemas']['PlanListItem'];
type Periodicite = components['schemas']['BillingInterval'];
type Genre = 'pack' | 'formule';

/**
 * Packs et abonnements (cahier des charges §7.8). Tout admin consulte ; créer,
 * renommer, changer le prix ou archiver une offre est réservé au super admin.
 * Un prix Stripe ne se modifie pas : changer le prix crée un nouveau tarif et
 * archive l'ancien.
 */

const PRIX_MIN = 50;
const MESSAGE_PRIX_MIN = 'Le prix doit être d’au moins 0,50 € (minimum imposé par Stripe).';

/** Message d'erreur d'une saisie de prix, ou null si elle est acceptable. */
export function erreurPrix(saisie: string, genre: Genre): string | null {
  const c = centimesDepuisSaisie(saisie);
  if (c === null) return 'Montant invalide (ex. : 9,90).';
  if (c === 0 && genre === 'formule') return null;
  if (c < PRIX_MIN) return genre === 'formule' ? `${MESSAGE_PRIX_MIN} Une formule peut aussi être gratuite (0 €).` : MESSAGE_PRIX_MIN;
  if (c > 1_000_000) return '10 000 € au plus.';
  return null;
}

const usePacks = () =>
  useQuery({ queryKey: ['ventes', 'packs'], queryFn: async () => (await lire(api.GET('/api/admin/billing/packs'))).packs });
const useFormules = () =>
  useQuery({ queryKey: ['ventes', 'formules'], queryFn: async () => (await lire(api.GET('/api/admin/billing/plans'))).plans });

function StatutOffre({ actif }: { actif: boolean }) {
  return actif ? <Badge tone="success">En vente</Badge> : <Badge>Archivé</Badge>;
}

function useCreation() {
  const [params, setParams] = useSearchParams();
  const ouvrir = (o: boolean) =>
    setParams(
      (a) => {
        const s = new URLSearchParams(a);
        if (o) s.set('creer', '1');
        else s.delete('creer');
        return s;
      },
      { replace: true }
    );
  return [params.get('creer') === '1', ouvrir] as const;
}

function NoteLectureSeule() {
  const { user } = useSession();
  if (user?.isSuperAdmin) return null;
  return <p className="note">Consultation seule : créer une offre, la renommer, en changer le prix ou l’archiver est réservé au super admin.</p>;
}

// ---------------------------------------------------------------------------
// Packs
// ---------------------------------------------------------------------------

export function PacksPage() {
  const { user } = useSession();
  const navigate = useNavigate();
  const { id: ouvert } = useParams();
  const [creer, setCreer] = useCreation();
  const packs = usePacks();

  const colonnes: Colonne<Pack>[] = [
    {
      id: 'nom',
      header: 'Pack',
      cell: (p) => (
        <div>
          <div className="cell-main">{p.name}</div>
          <div className="cell-sub">{p.code}</div>
        </div>
      ),
    },
    { id: 'genre', header: 'Type', cell: (p) => libelleDureePack(p.accessMonths) },
    { id: 'credits', header: 'Crédits', align: 'right', cell: (p) => <span className="num">{formatNombre(p.credits)}</span> },
    { id: 'prix', header: 'Prix', align: 'right', cell: (p) => <span className="num">{formatEuros(p.priceCents)}</span> },
    { id: 'unitaire', header: 'Prix par crédit', align: 'right', secondaire: true, cell: (p) => <span className="num">{formatPrixCredit(p.priceCents, p.credits)}</span> },
    { id: 'ventes', header: 'Ventes', align: 'right', cell: (p) => <Ventes stats={p.stats} /> },
    { id: 'encaisse', header: 'Encaissé', align: 'right', cell: (p) => <span className="num">{formatEuros(p.stats.revenueCents)}</span> },
    { id: 'statut', header: 'Statut', cell: (p) => <StatutOffre actif={p.isActive} /> },
  ];

  return (
    <>
      <VentesEntete
        lead="Achats en une fois : recharges, valables jusqu’à la fin de la période d’accès en cours, et renouvellements de l’accès (6 ou 12 mois, crédits sans report)."
        actions={
          user?.isSuperAdmin && (
            <Button variant="primary" icon={<Plus size={15} aria-hidden="true" />} onClick={() => setCreer(true)}>
              Créer un pack
            </Button>
          )
        }
      />
      <NoteLectureSeule />
      {packs.isError ? (
        <ErreurChargement erreur={packs.error} onReessayer={() => void packs.refetch()} />
      ) : (
        <DataTable
          libelle="Packs de crédits"
          colonnes={colonnes}
          lignes={packs.data}
          cleLigne={(p) => p.id}
          chargement={packs.isFetching && !packs.data}
          onOuvrir={(p) => navigate(`/ventes/packs/${p.id}`)}
          estActive={(p) => p.id === ouvert}
          vide={
            <EmptyState
              icon={<Package size={28} aria-hidden="true" />}
              title="Aucun pack"
              text="Rien n’est encore en vente : l’application élève affiche une liste vide."
              action={user?.isSuperAdmin ? <Button variant="primary" onClick={() => setCreer(true)}>Créer un pack</Button> : undefined}
            />
          }
        />
      )}
      <CreerOffre genre="pack" open={creer} onOpenChange={setCreer} />
      <Outlet />
    </>
  );
}

function Ventes({ stats }: { stats: Pack['stats'] }) {
  return (
    <span className="num" title={stats.refunds ? `dont ${stats.refunds} remboursée(s)` : undefined}>
      {formatNombre(stats.sales)}
      {stats.refunds > 0 && <span className="cell-sub"> · {formatNombre(stats.refunds)} remb.</span>}
    </span>
  );
}

export function PackDrawer() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const packs = usePacks();
  const p = packs.data?.find((x) => x.id === id);
  const fermer = () => navigate('/ventes/packs');

  return (
    <Drawer
      onClose={fermer}
      label={p ? `Pack ${p.name}` : 'Pack'}
      title={p ? p.name : <Skeleton width={200} height={22} />}
      subtitle={p ? <Code valeur={p.code} /> : undefined}
      badges={p ? <StatutOffre actif={p.isActive} /> : undefined}
    >
      {packs.isError ? (
        <div style={{ paddingTop: 16 }}>
          <ErreurChargement erreur={packs.error} onReessayer={() => void packs.refetch()} />
        </div>
      ) : !packs.data ? (
        <Skeleton height={200} />
      ) : !p ? (
        <EmptyState title="Pack introuvable" text="Il a peut-être été supprimé de la base." />
      ) : (
        <FicheOffre
          key={p.updatedAt}
          genre="pack"
          offre={{
            id: p.id,
            name: p.name,
            code: p.code,
            priceCents: p.priceCents,
            credits: p.credits,
            accessMonths: p.accessMonths,
            isActive: p.isActive,
            stripeProductId: p.stripeProductId,
            createdAt: p.createdAt,
            stats: p.stats,
          }}
        />
      )}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Formules d'abonnement
// ---------------------------------------------------------------------------

export function PlansPage() {
  const { user } = useSession();
  const navigate = useNavigate();
  const { id: ouvert } = useParams();
  const [creer, setCreer] = useCreation();
  const formules = useFormules();

  const colonnes: Colonne<Formule>[] = [
    {
      id: 'nom',
      header: 'Formule',
      cell: (f) => (
        <div>
          <div className="cell-main">{f.name}</div>
          <div className="cell-sub">{f.code}</div>
        </div>
      ),
    },
    { id: 'periodicite', header: 'Périodicité', cell: (f) => LIBELLE_PERIODICITE[f.interval] },
    { id: 'credits', header: 'Crédits par période', align: 'right', cell: (f) => <span className="num">{formatNombre(f.creditsPerPeriod)}</span> },
    { id: 'prix', header: 'Prix', align: 'right', cell: (f) => <span className="num">{f.priceCents === 0 ? 'Gratuite' : formatEuros(f.priceCents)}</span> },
    { id: 'unitaire', header: 'Prix par crédit', align: 'right', secondaire: true, cell: (f) => <span className="num">{formatPrixCredit(f.priceCents, f.creditsPerPeriod)}</span> },
    { id: 'abonnees', header: 'Abonnées', align: 'right', cell: (f) => <span className="num">{formatNombre(f.activeSubscribers)}</span> },
    { id: 'encaisse', header: 'Encaissé', align: 'right', secondaire: true, cell: (f) => <span className="num">{formatEuros(f.stats.revenueCents)}</span> },
    { id: 'statut', header: 'Statut', cell: (f) => <StatutOffre actif={f.isActive} /> },
  ];

  return (
    <>
      <VentesEntete
        lead="Formules mensuelles ou annuelles : des crédits versés à chaque période."
        actions={
          user?.isSuperAdmin && (
            <Button variant="primary" icon={<Plus size={15} aria-hidden="true" />} onClick={() => setCreer(true)}>
              Créer une formule
            </Button>
          )
        }
      />
      <NoteLectureSeule />
      {formules.isError ? (
        <ErreurChargement erreur={formules.error} onReessayer={() => void formules.refetch()} />
      ) : (
        <DataTable
          libelle="Formules d’abonnement"
          colonnes={colonnes}
          lignes={formules.data}
          cleLigne={(f) => f.id}
          chargement={formules.isFetching && !formules.data}
          onOuvrir={(f) => navigate(`/ventes/abonnements/${f.id}`)}
          estActive={(f) => f.id === ouvert}
          vide={
            <EmptyState
              icon={<Repeat size={28} aria-hidden="true" />}
              title="Aucune formule"
              text="Aucun abonnement n’est proposé aux élèves."
              action={user?.isSuperAdmin ? <Button variant="primary" onClick={() => setCreer(true)}>Créer une formule</Button> : undefined}
            />
          }
        />
      )}
      <CreerOffre genre="formule" open={creer} onOpenChange={setCreer} />
      <Outlet />
    </>
  );
}

export function PlanDrawer() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const formules = useFormules();
  const f = formules.data?.find((x) => x.id === id);
  const fermer = () => navigate('/ventes/abonnements');

  return (
    <Drawer
      onClose={fermer}
      label={f ? `Formule ${f.name}` : 'Formule'}
      title={f ? f.name : <Skeleton width={200} height={22} />}
      subtitle={f ? <Code valeur={f.code} /> : undefined}
      badges={
        f ? (
          <>
            <StatutOffre actif={f.isActive} />
            <Badge>{LIBELLE_PERIODICITE[f.interval]}</Badge>
          </>
        ) : undefined
      }
    >
      {formules.isError ? (
        <div style={{ paddingTop: 16 }}>
          <ErreurChargement erreur={formules.error} onReessayer={() => void formules.refetch()} />
        </div>
      ) : !formules.data ? (
        <Skeleton height={200} />
      ) : !f ? (
        <EmptyState title="Formule introuvable" />
      ) : (
        <FicheOffre
          key={f.updatedAt}
          genre="formule"
          offre={{
            id: f.id,
            name: f.name,
            code: f.code,
            priceCents: f.priceCents,
            credits: f.creditsPerPeriod,
            isActive: f.isActive,
            stripeProductId: f.stripeProductId,
            createdAt: f.createdAt,
            stats: f.stats,
            interval: f.interval,
            activeSubscribers: f.activeSubscribers,
          }}
        />
      )}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Fiche commune
// ---------------------------------------------------------------------------

interface Offre {
  id: string;
  name: string;
  code: string;
  priceCents: number;
  /** Crédits du pack, ou crédits par période de la formule. */
  credits: number;
  /** Pack : durée d'accès d'un renouvellement (mois), null pour une recharge. */
  accessMonths?: number | null;
  isActive: boolean;
  stripeProductId: string | null;
  createdAt: string;
  stats: Pack['stats'];
  interval?: Periodicite;
  activeSubscribers?: number;
}

function FicheOffre({ genre, offre: o }: { genre: Genre; offre: Offre }) {
  const id = useId();
  const { user } = useSession();
  const superAdmin = !!user?.isSuperAdmin;
  const toast = useToast();
  const queryClient = useQueryClient();
  const vue = useVentes();
  const [nom, setNom] = useState(o.name);
  const [credits, setCredits] = useState(String(o.credits));
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [prix, setPrix] = useState(false);
  const [archiver, setArchiver] = useState(false);

  const rafraichir = () =>
    Promise.all([queryClient.invalidateQueries({ queryKey: ['ventes'] }), queryClient.invalidateQueries({ queryKey: ['audit', o.id] })]);

  const nbCredits = Number(credits);
  const creditsValides = Number.isInteger(nbCredits) && nbCredits >= (genre === 'pack' ? 1 : 0) && credits !== '';
  const nomValide = nom.trim().length >= 2;
  const changements = {
    ...(nom.trim() !== o.name ? { name: nom.trim() } : {}),
    ...(creditsValides && nbCredits !== o.credits ? (genre === 'pack' ? { credits: nbCredits } : { creditsPerPeriod: nbCredits }) : {}),
  };
  const aChange = Object.keys(changements).length > 0;

  const patcher = (body: { name?: string; credits?: number; creditsPerPeriod?: number; isActive?: boolean }) =>
    genre === 'pack'
      ? lire(api.PATCH('/api/admin/billing/packs/{id}', { params: { path: { id: o.id } }, body }))
      : lire(api.PATCH('/api/admin/billing/plans/{id}', { params: { path: { id: o.id } }, body }));

  const enregistrer = async (e: FormEvent) => {
    e.preventDefault();
    if (!aChange || !nomValide || !creditsValides) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await patcher(changements);
      await rafraichir();
      toast.succes(genre === 'pack' ? 'Pack modifié.' : 'Formule modifiée.');
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  const mode = vue.data?.stripeMode;
  const produit = o.stripeProductId ? lienStripe(mode, `products/${o.stripeProductId}`) : null;
  const unite = genre === 'pack' ? '' : ` ${PAR_PERIODE[o.interval ?? 'MONTH']}`;

  return (
    <>
      <Section title="Offre">
        <InfoList
          items={[
            {
              label: 'Type',
              value: o.accessMonths
                ? `${libelleDureePack(o.accessMonths)} : ${o.accessMonths} mois d’accès et leurs crédits, ajoutés à la fin de l’accès en cours`
                : 'Recharge : crédits valables jusqu’à la fin de la période d’accès en cours',
              hidden: genre !== 'pack',
            },
            { label: 'Prix', value: o.priceCents === 0 ? 'Gratuite' : `${formatEuros(o.priceCents)}${unite}` },
            { label: genre === 'pack' ? 'Crédits' : 'Crédits par période', value: `${formatNombre(o.credits)}${unite}` },
            { label: 'Prix par crédit', value: formatPrixCredit(o.priceCents, o.credits) },
            { label: 'Abonnées actives', value: formatNombre(o.activeSubscribers ?? 0), hidden: genre === 'pack' },
            {
              label: 'Ventes',
              value: `${formatNombre(o.stats.sales)} paiement${o.stats.sales > 1 ? 's' : ''}${o.stats.refunds ? `, dont ${formatNombre(o.stats.refunds)} remboursé${o.stats.refunds > 1 ? 's' : ''}` : ''}`,
            },
            { label: 'Montant encaissé', value: `${formatEuros(o.stats.revenueCents)} (remboursements déduits)` },
            { label: 'Créé le', value: formatDateTime(o.createdAt) },
            {
              label: 'Produit Stripe',
              hidden: !o.stripeProductId,
              value: produit ? (
                <a className="link" href={produit} target="_blank" rel="noreferrer">
                  Ouvrir dans Stripe <ExternalLink size={12} aria-hidden="true" />
                </a>
              ) : (
                <code>{o.stripeProductId}</code>
              ),
            },
          ]}
        />
      </Section>

      <Section title="Carte vue par l’élève">
        <CarteOffre genre={genre} nom={o.name} credits={o.credits} priceCents={o.priceCents} interval={o.interval} />
        <p className="note">Aperçu indicatif : l’application élève affiche le nom, les crédits et le prix de l’offre.</p>
      </Section>

      {superAdmin && (
        <>
          <Section title="Modifier">
            <form className="stack" onSubmit={enregistrer}>
              <div className="grid-2">
                <Field id={`${id}-nom`} label="Nom" hint="Affiché à l’élève et sur la page de paiement Stripe." error={nomValide ? null : '2 caractères au moins.'}>
                  <Input id={`${id}-nom`} value={nom} onChange={(e) => setNom(e.target.value)} maxLength={80} invalid={!nomValide} />
                </Field>
                <Field
                  id={`${id}-credits`}
                  label={genre === 'pack' ? 'Crédits' : 'Crédits par période'}
                  hint={genre === 'pack' ? 'Pour les prochains achats.' : 'Versés aux prochains renouvellements.'}
                  error={creditsValides ? null : 'Nombre entier attendu.'}
                >
                  <Input id={`${id}-credits`} inputMode="numeric" value={credits} onChange={(e) => setCredits(e.target.value.replace(/[^\d]/g, ''))} invalid={!creditsValides} />
                </Field>
              </div>
              <p className="note">Le code ({o.code}) est définitif.</p>
              {erreur && <Alert>{erreur}</Alert>}
              <div className="row">
                <Button type="submit" variant="primary" loading={envoi} disabled={!aChange || !nomValide || !creditsValides}>
                  Enregistrer
                </Button>
              </div>
            </form>
          </Section>

          <Section title="Actions">
            <div className="actions-list">
              <Button icon={<Tag size={15} aria-hidden="true" />} onClick={() => setPrix(true)}>
                Changer le prix
              </Button>
              {o.isActive ? (
                <Button icon={<Archive size={15} aria-hidden="true" />} onClick={() => setArchiver(true)}>
                  Archiver
                </Button>
              ) : (
                <Button icon={<ArchiveRestore size={15} aria-hidden="true" />} onClick={() => setArchiver(true)}>
                  Remettre en vente
                </Button>
              )}
            </div>
          </Section>
        </>
      )}

      <Section title="Journal">
        <AuditTimeline targetId={o.id} />
      </Section>

      <ChangerPrix genre={genre} offre={o} open={prix} onOpenChange={setPrix} />

      <ConfirmDialog
        open={archiver}
        onOpenChange={setArchiver}
        title={o.isActive ? `Archiver ${genre === 'pack' ? 'le pack' : 'la formule'}` : 'Remettre en vente'}
        confirmLabel={o.isActive ? 'Archiver' : 'Remettre en vente'}
        tone={o.isActive ? 'danger' : 'primary'}
        onConfirm={async () => {
          await patcher({ isActive: !o.isActive });
          await rafraichir();
          toast.succes(o.isActive ? 'Offre archivée.' : 'Offre remise en vente.');
        }}
      >
        {o.isActive ? (
          <p>
            « {o.name} » n’est plus proposé à l’achat.{' '}
            {genre === 'formule' && (o.activeSubscribers ?? 0) > 0
              ? `Les ${formatNombre(o.activeSubscribers ?? 0)} abonnées en cours ne sont pas résiliées : elles se désabonnent depuis leur espace Stripe.`
              : 'Les achats passés ne changent pas.'}
          </p>
        ) : (
          <p>« {o.name} » est de nouveau proposé aux élèves, au prix de {formatEuros(o.priceCents)}.</p>
        )}
      </ConfirmDialog>
    </>
  );
}

/** Carte de l'offre telle que l'application élève la présente (aperçu). */
export function CarteOffre({
  genre,
  nom,
  credits,
  priceCents,
  interval,
}: {
  genre: Genre;
  nom: string;
  credits: number;
  priceCents: number;
  interval?: Periodicite | undefined;
}) {
  const periode = interval === 'YEAR' ? 'an' : 'mois';
  return (
    <div className="carte-offre" role="img" aria-label={`Aperçu : ${nom}, ${credits} crédits, ${formatEuros(priceCents)}`}>
      <div className="carte-offre__nom">{nom || 'Nom de l’offre'}</div>
      <div className="carte-offre__credits">
        {formatNombre(credits)} crédits{genre === 'formule' ? ` par ${periode}` : ''}
      </div>
      <div className="carte-offre__prix">
        {priceCents === 0 ? 'Gratuit' : formatEuros(priceCents)}
        {genre === 'formule' && priceCents > 0 && <span> / {periode}</span>}
      </div>
      {priceCents > 0 && <div className="carte-offre__unitaire">soit {formatPrixCredit(priceCents, credits)} le crédit</div>}
      <span className="carte-offre__bouton" aria-hidden="true">
        {genre === 'pack' ? 'Acheter' : 'S’abonner'}
      </span>
    </div>
  );
}

function ChangerPrix({ genre, offre: o, open, onOpenChange }: { genre: Genre; offre: Offre; open: boolean; onOpenChange: (o: boolean) => void }) {
  const id = useId();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [saisie, setSaisie] = useState('');
  const [touche, setTouche] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreurApi, setErreurApi] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSaisie(saisieEuros(o.priceCents));
      setTouche(false);
      setErreurApi(null);
    }
  }, [open, o.priceCents]);

  const erreur = erreurPrix(saisie, genre);
  const centimes = centimesDepuisSaisie(saisie);
  const identique = centimes === o.priceCents;
  const gardees = o.activeSubscribers ?? 0;

  const valider = async (e: FormEvent) => {
    e.preventDefault();
    setTouche(true);
    if (erreur || centimes === null || identique) return;
    setEnvoi(true);
    setErreurApi(null);
    try {
      if (genre === 'pack') {
        await lire(api.POST('/api/admin/billing/packs/{id}/price', { params: { path: { id: o.id } }, body: { priceCents: centimes } }));
        toast.succes(`Nouveau prix : ${formatEuros(centimes)}.`);
      } else {
        const r = await lire(api.POST('/api/admin/billing/plans/{id}/price', { params: { path: { id: o.id } }, body: { priceCents: centimes } }));
        toast.succes(
          r.subscribersKeepingOldPrice > 0
            ? `Nouveau prix : ${formatEuros(centimes)}. ${formatNombre(r.subscribersKeepingOldPrice)} abonnée(s) gardent l’ancien prix jusqu’au renouvellement.`
            : `Nouveau prix : ${formatEuros(centimes)}.`
        );
      }
      await Promise.all([queryClient.invalidateQueries({ queryKey: ['ventes'] }), queryClient.invalidateQueries({ queryKey: ['audit', o.id] })]);
      onOpenChange(false);
    } catch (err) {
      setErreurApi(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={(v) => !envoi && onOpenChange(v)} title={`Changer le prix — ${o.name}`}>
      <form className="stack" onSubmit={valider} noValidate>
        <p className="confirm__impact">
          Prix actuel : <strong>{o.priceCents === 0 ? 'gratuite' : formatEuros(o.priceCents)}</strong>. Un nouveau prix est créé chez Stripe et
          l’ancien archivé ; {genre === 'pack' ? 'les achats passés gardent leur montant.' : 'les paiements passés ne changent pas.'}
        </p>
        {genre === 'formule' && gardees > 0 && (
          <Alert tone="info">
            <strong>{formatNombre(gardees)}</strong> abonnée{gardees > 1 ? 's' : ''} en cours {gardees > 1 ? 'gardent' : 'garde'} l’ancien prix jusqu’à
            leur prochain renouvellement : Stripe ne modifie pas un abonnement existant.
          </Alert>
        )}
        <Field
          id={`${id}-prix`}
          label="Nouveau prix (€)"
          hint={genre === 'formule' ? 'Au moins 0,50 €, ou 0 pour une formule gratuite.' : 'Au moins 0,50 € (minimum Stripe).'}
          error={touche && erreur ? erreur : touche && identique ? 'C’est déjà le prix actuel.' : null}
        >
          <Input
            id={`${id}-prix`}
            inputMode="decimal"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            onBlur={() => setTouche(true)}
            invalid={touche && (!!erreur || identique)}
            autoFocus
          />
        </Field>
        {centimes !== null && !erreur && centimes > 0 && (
          <p className="note">Soit {formatPrixCredit(centimes, o.credits)} le crédit.</p>
        )}
        {erreurApi && <Alert>{erreurApi}</Alert>}
        <div className="modal__footer modal__footer--inline">
          <Button onClick={() => onOpenChange(false)} disabled={envoi}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={envoi}>
            Changer le prix
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Création
// ---------------------------------------------------------------------------

const CODE = /^[A-Z0-9_-]{2,40}$/;

function CreerOffre({ genre, open, onOpenChange }: { genre: Genre; open: boolean; onOpenChange: (o: boolean) => void }) {
  const id = useId();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [nom, setNom] = useState('');
  const [credits, setCredits] = useState('');
  const [prix, setPrix] = useState('');
  const [periodicite, setPeriodicite] = useState<Periodicite>('MONTH');
  const [duree, setDuree] = useState('');
  const [touche, setTouche] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreurApi, setErreurApi] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCode('');
      setNom('');
      setCredits('');
      setPrix('');
      setPeriodicite('MONTH');
      setDuree('');
      setTouche(false);
      setErreurApi(null);
    }
  }, [open]);

  const nbCredits = Number(credits);
  const erreurs = {
    code: CODE.test(code) ? null : 'Majuscules, chiffres, « _ » ou « - », 2 à 40 caractères (ex. : RECHARGE_100).',
    nom: nom.trim().length >= 2 ? null : '2 caractères au moins.',
    credits: credits !== '' && Number.isInteger(nbCredits) && nbCredits >= (genre === 'pack' ? 1 : 0) ? null : 'Nombre entier attendu.',
    prix: erreurPrix(prix, genre),
  };
  const valide = Object.values(erreurs).every((e) => e === null);
  const centimes = centimesDepuisSaisie(prix);

  const creer = async (e: FormEvent) => {
    e.preventDefault();
    setTouche(true);
    if (!valide || centimes === null) return;
    setEnvoi(true);
    setErreurApi(null);
    try {
      if (genre === 'pack') {
        const r = await lire(
          api.POST('/api/admin/billing/packs', {
            body: { code, name: nom.trim(), credits: nbCredits, priceCents: centimes, accessMonths: duree ? Number(duree) : null },
          })
        );
        await queryClient.invalidateQueries({ queryKey: ['ventes'] });
        toast.succes(`Pack « ${r.pack.name} » en vente.`);
        navigate(`/ventes/packs/${r.pack.id}`);
      } else {
        const r = await lire(
          api.POST('/api/admin/billing/plans', {
            body: { code, name: nom.trim(), creditsPerPeriod: nbCredits, priceCents: centimes, interval: periodicite },
          })
        );
        await queryClient.invalidateQueries({ queryKey: ['ventes'] });
        toast.succes(`Formule « ${r.plan.name} » en vente.`);
        navigate(`/ventes/abonnements/${r.plan.id}`);
      }
    } catch (err) {
      setErreurApi(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  const e = (champ: keyof typeof erreurs) => (touche ? erreurs[champ] : null);

  return (
    <Modal open={open} onOpenChange={(v) => !envoi && onOpenChange(v)} title={genre === 'pack' ? 'Créer un pack' : 'Créer une formule'} width={620}>
      <form className="stack" onSubmit={creer} noValidate>
        <div className="grid-2">
          <Field id={`${id}-code`} label="Code" hint="Définitif : l’application élève achète par ce code." error={e('code')}>
            <Input
              id={`${id}-code`}
              value={code}
              onChange={(ev) => setCode(ev.target.value.toUpperCase().replace(/\s/g, '_'))}
              maxLength={40}
              placeholder={genre === 'pack' ? 'RECHARGE_100' : 'PRO_MENSUEL'}
              invalid={!!e('code')}
              autoFocus
            />
          </Field>
          <Field id={`${id}-nom`} label="Nom" error={e('nom')}>
            <Input id={`${id}-nom`} value={nom} onChange={(ev) => setNom(ev.target.value)} maxLength={80} placeholder={genre === 'pack' ? 'Recharge 100 crédits' : 'Pro mensuel'} invalid={!!e('nom')} />
          </Field>
          <Field id={`${id}-credits`} label={genre === 'pack' ? 'Crédits' : 'Crédits par période'} error={e('credits')}>
            <Input id={`${id}-credits`} inputMode="numeric" value={credits} onChange={(ev) => setCredits(ev.target.value.replace(/[^\d]/g, ''))} invalid={!!e('credits')} />
          </Field>
          <Field
            id={`${id}-prix`}
            label="Prix (€)"
            hint={genre === 'pack' ? 'Au moins 0,50 € (minimum Stripe).' : 'Au moins 0,50 €, ou 0 pour une formule gratuite.'}
            error={e('prix')}
          >
            <Input id={`${id}-prix`} inputMode="decimal" value={prix} onChange={(ev) => setPrix(ev.target.value)} placeholder="9,90" invalid={!!e('prix')} />
          </Field>
          {genre === 'pack' && (
            <Field
              id={`${id}-duree`}
              label="Type"
              hint={
                duree
                  ? 'Ajoute la durée à l’accès de l’élève (à la fin de la période en cours, ou tout de suite si son accès est terminé). Crédits sans report.'
                  : 'Crédits valables jusqu’à la fin de la période d’accès en cours. Plus en vente pour une élève dont l’accès est terminé.'
              }
            >
              <Select id={`${id}-duree`} value={duree} onChange={(ev) => setDuree(ev.target.value)}>
                <option value="">Recharge</option>
                <option value="6">Renouvellement 6 mois</option>
                <option value="12">Renouvellement 12 mois</option>
              </Select>
            </Field>
          )}
          {genre === 'formule' && (
            <Field id={`${id}-periode`} label="Périodicité">
              <Select id={`${id}-periode`} value={periodicite} onChange={(ev) => setPeriodicite(ev.target.value as Periodicite)}>
                <option value="MONTH">Mensuelle</option>
                <option value="YEAR">Annuelle</option>
              </Select>
            </Field>
          )}
        </div>
        <div>
          <div className="ui-field__label" style={{ marginBottom: 6 }}>
            Carte vue par l’élève
          </div>
          <CarteOffre genre={genre} nom={nom.trim()} credits={Number.isInteger(nbCredits) ? nbCredits : 0} priceCents={centimes ?? 0} interval={periodicite} />
        </div>
        <p className="note">Le produit et son prix sont créés chez Stripe ; l’offre est en vente dès sa création.</p>
        {erreurApi && <Alert>{erreurApi}</Alert>}
        <div className="modal__footer modal__footer--inline">
          <Button onClick={() => onOpenChange(false)} disabled={envoi}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={envoi}>
            {genre === 'pack' ? 'Créer le pack' : 'Créer la formule'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
